import classNames from 'classnames';
import { CSSProperties, forwardRef, useMemo } from 'react';
import type { ColorsTheme } from '@alltrails/types/ColorsTheme';
import type { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkInfoRenderer from '../LinkInfoRenderer';
import LoadingSpinner from '../LoadingSpinner';
import styles from './styles/IconButton.module.scss';

export type IconButtonVariant = 'accent' | 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'insetDark' | 'insetLight';

type IconButtonProps = {
  className?: string;
  'data-denali'?: string;
  disabled?: boolean;
  elevated?: boolean;
  icon: IconDefinition<'orientation' | 'color'>;
  linkInfo?: LinkInfo;
  loading?: boolean;
  onBlur?: () => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  onFocus?: () => void;
  size?: Size<'xs' | 'sm' | 'md' | 'lg'>;
  stopPropagation?: boolean;
  style?: CSSProperties;
  tabIndex?: number;
  theme?: ColorsTheme;
  title: string;
  ariaLabel?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaDescribedBy?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: IconButtonVariant;
  dataTooltipId?: string;
};

const IconButton = forwardRef(
  (
    {
      className,
      'data-denali': dataDenali,
      disabled,
      elevated = false,
      icon,
      linkInfo,
      loading,
      onBlur,
      onClick,
      onKeyDown,
      onFocus,
      size = 'md',
      stopPropagation = false,
      style,
      tabIndex,
      theme, // Do not default this since it overrides a wrapping component's theme
      title,
      ariaLabel,
      ariaControls,
      ariaExpanded,
      ariaDescribedBy,
      type = 'button',
      variant = 'secondary',
      dataTooltipId
    }: IconButtonProps,
    ref: React.Ref<HTMLElement>
  ) => {
    const onButtonClick = useMemo(
      () => (e: React.MouseEvent<HTMLElement>) => {
        if (stopPropagation) {
          e.stopPropagation();
        }
        if (onClick) {
          onClick(e);
        }
      },
      [onClick, stopPropagation]
    );
    const handleKeyDown = useMemo(
      () => (e: React.KeyboardEvent<HTMLElement>) => {
        if (stopPropagation) {
          e.stopPropagation();
        }
        if (onKeyDown) {
          onKeyDown(e);
        }
      },
      [onKeyDown, stopPropagation]
    );
    const iconSize = size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : 'md';
    const spinnerSize = size === 'xs' || size === 'sm' ? 'sm' : 'md';
    const content = useMemo(() => {
      if (loading && !disabled) {
        return (
          <>
            <LoadingSpinner size={spinnerSize} />
          </>
        );
      }

      return (
        <>
          <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: iconSize }} />
        </>
      );
    }, [disabled, icon, iconSize, spinnerSize, loading]);

    const classes = classNames(
      className,
      styles.button,
      styles[size],
      styles[variant],
      elevated && styles.elevated,
      loading && styles.loading,
      disabled && styles.disabled // An anchor tag cannot be disabled, so we apply a disabled class instead
    );

    const buttonElement = useMemo(() => {
      if (linkInfo) {
        if (disabled) {
          return (
            <span className={classes} {...setDenaliAttribute(dataDenali || 'IconButton')} title={title} aria-label={ariaLabel || title} ref={ref}>
              {content}
            </span>
          );
        }

        return (
          <LinkInfoRenderer
            className={classes}
            {...setDenaliAttribute(dataDenali || 'IconButton')}
            dataTooltipId={dataTooltipId}
            linkInfo={linkInfo}
            onClick={loading ? undefined : onButtonClick}
            onKeyDown={loading ? undefined : handleKeyDown}
            title={title}
            ariaLabel={ariaLabel || title}
            tabIndex={tabIndex}
            ref={ref as React.Ref<HTMLAnchorElement>}
          >
            {content}
          </LinkInfoRenderer>
        );
      }

      return (
        <button
          {...(theme ? { 'data-at-theme-colors': theme } : {})}
          className={classes}
          {...setDenaliAttribute(dataDenali || 'IconButton')}
          onClick={loading ? undefined : onButtonClick}
          onKeyDown={loading ? undefined : handleKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          title={title}
          aria-busy={loading && !disabled ? true : undefined}
          aria-label={ariaLabel || title}
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-describedby={ariaDescribedBy}
          type={type}
          tabIndex={tabIndex}
          data-tooltip-id={dataTooltipId}
          ref={ref as React.Ref<HTMLButtonElement>}
          style={style}
        >
          {content}
        </button>
      );
    }, [
      linkInfo,
      theme,
      classes,
      dataDenali,
      loading,
      onButtonClick,
      handleKeyDown,
      onBlur,
      onFocus,
      disabled,
      title,
      ariaLabel,
      ariaControls,
      ariaExpanded,
      ariaDescribedBy,
      type,
      tabIndex,
      dataTooltipId,
      ref,
      content,
      style
    ]);

    return buttonElement;
  }
);

export default IconButton;
